import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import * as sched from 'sched';
import { useWatchlist } from '@/hooks/useWatchlist';

interface WatchButtonProps {
  workspaceId: string | undefined;
  target: sched.WatchTarget;
  /** Optional descriptive label next to the icon. Defaults to "Watch" / "Watching". */
  showLabel?: boolean;
}

/**
 * Toggle watching a Task / Outcome / Team on the current user's watchlist.
 * Renders as a pill-style button matching the style of the completion toggle
 * and other header actions on detail pages.
 */
const WatchButton: React.FC<WatchButtonProps> = ({ workspaceId, target, showLabel = true }) => {
  const { addWatch, removeWatch, isWatching } = useWatchlist(workspaceId);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const watching = isWatching(target);

  const onToggle = async () => {
    if (!workspaceId) return;
    setPending(true);
    setErrorMessage(null);
    try {
      if (watching) {
        await removeWatch(target);
      } else {
        await addWatch(target);
      }
    } catch (e: any) {
      setErrorMessage(e?.message ?? 'Failed to update watchlist');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onToggle}
        disabled={pending || !workspaceId}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-sm transition-colors border ${
          watching
            ? 'bg-cm-accent-teal/15 text-cm-accent-teal border-cm-accent-teal/40 hover:bg-cm-accent-teal/25'
            : 'bg-cm-bg-deep text-cm-text-secondary border-cm-border-subtle hover:border-cm-accent-teal hover:text-cm-accent-teal'
        } ${pending ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
        aria-label={watching ? 'Stop watching' : 'Watch'}
        aria-pressed={watching}
      >
        {watching ? <FiEye className="w-4 h-4" /> : <FiEyeOff className="w-4 h-4" />}
        {showLabel && <span>{watching ? 'Watching' : 'Watch'}</span>}
      </button>
      {errorMessage && (
        <span className="text-xs text-red-400" role="alert">
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default WatchButton;
